import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Search, Mail, Phone, MessageSquare, LifeBuoy, BookOpen, Send, CheckCircle, AlertCircle } from "lucide-react";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import axios from "axios";

export default function HelpPage() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: ""
  });
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    const uStr = localStorage.getItem("user") || sessionStorage.getItem("user");
    if (uStr) {
      try {
        const user = JSON.parse(uStr);
        setFormData(prev => ({
          ...prev,
          name: `${user.name || ""} ${user.lastName || ""}`.trim(),
          email: user.email || ""
        }));
      } catch (e) {
        console.error("Error parsing user:", e);
      }
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus(null);

    const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");

    try {
      await axios.post(`${apiUrl}/api/support`, formData, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setStatus({
        type: "success",
        text: "Thank you! Your message has been sent successfully. We will reply to your email."
      });
      setFormData(prev => ({
        ...prev,
        subject: "",
        message: ""
      }));
    } catch (err) {
      console.error(err);
      setStatus({
        type: "error",
        text: err.response?.data?.error || "Failed to send message. Please try again later."
      });
    } finally {
      setLoading(false);
    }
  };

  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const categories = [
    {
      title: "Getting Started",
      description: "Learn how to create an account, verify your profile, and start browsing.",
      icon: BookOpen
    },
    {
      title: "Buying Guide",
      description: "Tips on how to safely interact with sellers and secure your dream items.",
      icon: Search
    },
    {
      title: "Selling on HuluOrder",
      description: "Everything you need to know about listing items and reaching more buyers.",
      icon: LifeBuoy
    }
  ];

  return (
    <div className="min-h-screen bg-white font-sans text-gray-900">
      <Header />

      <main>
        {/* Hero Section */}
        <section className="relative py-20 overflow-hidden bg-gray-50">
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-gray-900 via-gray-900 to-green-900/30 -z-10"></div>
          <div className="max-w-7xl mx-auto px-6 text-center">
            <motion.h1 
              {...fadeIn}
              className="text-4xl md:text-6xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-green-400 to-emerald-400"
            >
              Help Center
            </motion.h1>
            <motion.p 
              {...fadeIn}
              transition={{ delay: 0.2 }}
              className="text-lg text-gray-400 max-w-2xl mx-auto mb-10"
            >
              Have a question? We're here to help you navigate Ethiopia's 
              premier marketplace.
            </motion.p>
            
            {/* Search Bar */}
            <motion.div 
              {...fadeIn}
              transition={{ delay: 0.3 }}
              className="max-w-xl mx-auto relative"
            >
              <input 
                type="text" 
                placeholder="Search for help..." 
                className="w-full px-6 py-4 rounded-2xl bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 backdrop-blur-sm"
              />
              <Search className="absolute right-6 top-4 w-6 h-6 text-gray-400" />
            </motion.div>
          </div>
        </section>

        {/* Categories Section */}
        <section className="py-24 max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20">
            {categories.map((cat, i) => (
              <motion.div 
                key={i}
                whileHover={{ y: -5 }}
                className="p-8 rounded-3xl border border-gray-100 shadow-sm hover:shadow-lg transition-all"
              >
                <div className="w-14 h-14 bg-green-100 rounded-2xl flex items-center justify-center mb-6">
                  <cat.icon className="w-8 h-8 text-green-600" />
                </div>
                <h2 className="text-xl font-bold mb-4">{cat.title}</h2>
                <p className="text-gray-600 leading-relaxed text-sm">
                  {cat.description}
                </p>
              </motion.div>
            ))}
          </div>

          {/* Contact Support */}
          <div className="bg-gray-900 rounded-[3rem] p-12 text-center text-white relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-green-900/20 to-transparent pointer-events-none"></div>
            <h2 className="text-3xl font-bold mb-8">Can't find what you're looking for?</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <a href="mailto:support@huluorder.com" className="flex flex-col items-center gap-3 hover:text-green-400 transition-colors">
                <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center">
                  <Mail className="w-6 h-6 text-green-400" />
                </div>
                <p className="font-medium">support@huluorder.com</p>
              </a>
              <a href="tel:+251906103333" className="flex flex-col items-center gap-3 hover:text-green-400 transition-colors">
                <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center">
                  <Phone className="w-6 h-6 text-green-400" />
                </div>
                <p className="font-medium">+251 90 6103 333</p>
              </a>
              <div className="flex flex-col items-center gap-3">
                <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center">
                  <MessageSquare className="w-6 h-6 text-green-400" />
                </div>
                <p className="font-medium">Live Chat (9AM - 6PM)</p>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Form Section */}
        <section className="py-16 max-w-3xl mx-auto px-6">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-white rounded-[2.5rem] border border-gray-100 shadow-xl p-8 md:p-12 relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-64 h-64 bg-green-50 rounded-full blur-3xl -z-10 opacity-60"></div>
            
            <div className="text-center mb-10">
              <h2 className="text-3xl font-extrabold text-gray-900 mb-3">Send us a Message</h2>
              <p className="text-gray-500 text-sm max-w-md mx-auto">
                Have specific questions or need support? Fill out the form below and our team will get back to you directly.
              </p>
            </div>

            {status && (
              <div className={`p-4 mb-8 rounded-2xl flex items-start gap-3 text-sm font-medium ${
                status.type === "success" 
                  ? "bg-green-50 text-green-800 border border-green-100" 
                  : "bg-red-50 text-red-800 border border-red-100"
              }`}>
                {status.type === "success" ? (
                  <CheckCircle className="w-5 h-5 shrink-0 text-green-600 mt-0.5" />
                ) : (
                  <AlertCircle className="w-5 h-5 shrink-0 text-red-600 mt-0.5" />
                )}
                <p>{status.text}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-400 mb-2">Your Name</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Enter your name"
                    className="w-full px-5 py-4 rounded-2xl bg-gray-50 border-transparent focus:border-green-500 focus:bg-white focus:ring-0 text-sm outline-none transition-all border shadow-inner"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-400 mb-2">Email Address</label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="Enter your email"
                    className="w-full px-5 py-4 rounded-2xl bg-gray-50 border-transparent focus:border-green-500 focus:bg-white focus:ring-0 text-sm outline-none transition-all border shadow-inner"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-400 mb-2">Subject</label>
                <input
                  type="text"
                  required
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  placeholder="What can we help you with?"
                  className="w-full px-5 py-4 rounded-2xl bg-gray-50 border-transparent focus:border-green-500 focus:bg-white focus:ring-0 text-sm outline-none transition-all border shadow-inner"
                />
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-400 mb-2">Message</label>
                <textarea
                  required
                  rows="5"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="Describe your inquiry details..."
                  className="w-full px-5 py-4 rounded-2xl bg-gray-50 border-transparent focus:border-green-500 focus:bg-white focus:ring-0 text-sm outline-none transition-all border shadow-inner resize-none"
                ></textarea>
              </div>

              <div className="pt-2">
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-4 bg-green-600 text-white rounded-2xl font-bold hover:bg-green-700 active:scale-[0.98] transition-all disabled:opacity-50 flex items-center justify-center gap-2 shadow-lg shadow-green-200 text-sm cursor-pointer"
                >
                  <Send className="w-4 h-4" />
                  {loading ? "Sending Message..." : "Send Message"}
                </button>
              </div>
            </form>
          </motion.div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
